import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Loader2, AlertCircle } from 'lucide-react';
import ModalWrapper from '../ModalWrapper';
import { toast } from 'react-toastify';

export default function SecurityVerificationModal({ onClose, onConfirm, onGetCode, onSuccess }) {
    const [code, setCode] = useState('');
    const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'confirmed'
    const [isGettingCode, setIsGettingCode] = useState(false);
    const [error, setError] = useState('');

    const handleGetCode = async (callback) => {
        setIsGettingCode(true);
        setError('');
        try {
            if (onGetCode) {
                await onGetCode()
            } else {
                await new Promise(resolve => setTimeout(resolve, 2000))
            }
            callback?.()
        } catch (err) {
            toast.error(err.message || "Failed to send code. Please try again.");
        } finally {
            setIsGettingCode(false);
        }
    };

    // const validateCode = (value) => {
    //     // Only allow digits
    //     const digitsOnly = value.replace(/[^\d]/g, '');
    //     if (digitsOnly !== value) {
    //         setError('Only numbers are allowed');
    //         return false;
    //     }
        
    //     if (digitsOnly.length > 6) {
    //         setError('Code cannot exceed 6 digits');
    //         return false;
    //     }
        
    //     if (digitsOnly.length > 0 && digitsOnly.length < 6) {
    //         setError('Code must be 6 digits');
    //         return false;
    //     }
        
    //     if (digitsOnly.length === 6) {
    //         setError('');
    //         return true;
    //     }
        
    //     setError('');
    //     return false;
    // };

    const handleCodeChange = (e) => {
        const value = e.target.value;
        // Only allow digits and limit to 6 characters
        const digitsOnly = value.replace(/[^\d]/g, '').slice(0, 6);
        setCode(digitsOnly);
        
        if (digitsOnly.length > 0 && digitsOnly.length < 6) {
            setError('Code must be 6 digits');
        } else if (digitsOnly.length === 6) {
            setError('');
        } else {
            setError('');
        }
    };

    const handleConfirm = async () => {
        // Validate before confirming
        if (code.length !== 6) {
            setError('Please enter a valid 6-digit code');
            toast.error('Please enter a valid 6-digit code');
            return;
        }

        try {
            setStatus('loading');
            setError('');
            
            if (onConfirm) {
                await onConfirm(code)
            } else {
                await new Promise((resolve) => setTimeout(resolve, 2000))
            }
            
            setCode("")
            setStatus('confirmed');
            setTimeout(() => {
                onSuccess();
            }, 1000);
        }
        catch (error) {
            setStatus("idle")
            const errorMessage = error.message || "Invalid verification code. Please try again.";
            setError(errorMessage);
            toast.error(errorMessage);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && code.length === 6 && status === 'idle') {
            handleConfirm();
        }
    };

    return (
        <ModalWrapper onClose={onClose} showClose={false}>
            <div className="p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-semibold mb-2">Security verification</h2>
                <p className="text-gray-400 text-xs sm:text-sm mb-6">Enter the 6-digit code sent to your email</p>

                <div className="relative mb-2">
                    <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={code}
                        onChange={handleCodeChange}
                        onKeyPress={handleKeyPress}
                        placeholder="Enter 6-digit code"
                        disabled={status === 'confirmed' || status === "loading"}
                        maxLength={6}
                        className={`w-full bg-[#4a4a4a] border rounded-lg p-3 pr-24 text-white placeholder-gray-500 focus:outline-none focus:border-[#c9a227] text-base sm:text-lg tracking-wider font-mono
                            ${status === 'confirmed' ? 'border-green-500' : ''}
                            ${error && status !== 'confirmed' ? 'border-red-500 focus:border-red-500' : 'border-[#5a5a5a]'}
                        `}
                        autoFocus
                    />

                    {status === 'loading' && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                            >
                                <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 text-[#c9a227]" />
                            </motion.div>
                        </div>
                    )}

                    {status === 'confirmed' && (
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 text-green-500">
                            <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                            <span className="text-xs sm:text-sm">Confirmed</span>
                        </div>
                    )}

                    <ResendCountDown
                        handleGetCode={handleGetCode}
                        isGettingCode={isGettingCode}
                        status={status}
                    />
                </div>

                {/* Error Message */}
                {error && status !== 'confirmed' && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-4 p-2 bg-red-500/10 border border-red-500/50 rounded-lg flex items-center gap-2"
                    >
                        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <p className="text-red-500 text-xs sm:text-sm">{error}</p>
                    </motion.div>
                )}

                {/* Code length indicator */}
                {code.length > 0 && code.length < 6 && status === 'idle' && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="mb-4 text-xs text-gray-400"
                    >
                        {code.length}/6 digits entered
                    </motion.div>
                )}

                <button
                    onClick={handleConfirm}
                    disabled={code.length !== 6 || status !== 'idle'}
                    className={`w-full py-3 rounded-lg font-semibold transition-colors text-sm sm:text-base
                        ${code.length === 6 && status === 'idle'
                            ? 'bg-[#c9a227] hover:bg-[#b8921f] text-black'
                            : 'bg-[#3a3a3a] text-gray-500 cursor-not-allowed'
                        }`}
                >
                    {status === 'loading' ? (
                        <div className="flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Verifying...</span>
                        </div>
                    ) : status === 'confirmed' ? (
                        'Verified!'
                    ) : (
                        'Confirm'
                    )}
                </button>

                {/* Help text */}
                <p className="text-center text-gray-500 text-xs mt-4">
                    Didn't receive the code? Check your spam folder or request a new one.
                </p>
            </div>
        </ModalWrapper>
    );
}


const ResendCountDown = ({ handleGetCode, isGettingCode, status }) => {
    const [countdown, setCountdown] = useState(60);
    const [isCounting, setIsCounting] = useState(true);

    useEffect(() => {
        let timer;

        if (isCounting && countdown > 0) {
            timer = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
        }

        if (countdown === 0 && isCounting) {
            setIsCounting(false);
        }

        return () => clearInterval(timer);
    }, [countdown, isCounting]);

    const resetCountDown = () => {
        setCountdown(60);
        setIsCounting(true);
    }

    if (status === "loading" || status === "confirmed") {
        return null
    }

    return (
        <button
            onClick={() => handleGetCode(resetCountDown)}
            disabled={isGettingCode || isCounting || status !== 'idle'}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#c9a227] text-xs sm:text-sm hover:underline disabled:hover:no-underline disabled:opacity-50 whitespace-nowrap"
        >
            {isGettingCode
                ? 'Sending...'
                : isCounting
                    ? `Resend in ${countdown}s`
                    : 'Get Code'
            }
        </button>
    );
}